import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Loader2, Package } from 'lucide-react';
import StoreNav from '../components/store/StoreNav';
import PublicSiteFooter from '../components/store/PublicSiteFooter';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useProductCategories } from '../hooks/useProductCategories';
import { defaultPricingRegion, formatProductPrice } from '../lib/productPricing';
import { resolveThumbnailUrl } from '../lib/publicApi';
import {
  buildStoreNavItems,
  categoryIdToPath,
  categorySlugToId,
} from '../lib/templateStoreCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SubcategoryNav({ items, activeId, categories, onNavigate }) {
  return (
    <nav className="space-y-1" aria-label="Browse by category">
      {items.map((item) => {
        const selected = activeId === item.id;
        return (
          <Link
            key={item.id}
            to={categoryIdToPath(item.id, categories)}
            onClick={() => onNavigate?.()}
            aria-current={selected ? 'page' : undefined}
            className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition ${
              selected
                ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                : 'text-muted-foreground hover:bg-white hover:text-foreground ring-1 ring-transparent hover:ring-border'
            }`}
            data-testid={`subcategory-nav-${item.id}`}
          >
            <span>{item.label}</span>
            <span className={`tabular-nums text-xs ${selected ? 'text-primary-foreground/90' : 'text-muted-foreground'}`}>
              {item.count}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

function SidebarCategoriesSkeleton() {
  return (
    <div className="space-y-2 px-3" aria-hidden="true">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

export default function TemplatesListingPage({ categorySlug = null, categories: categoriesProp = null }) {
  const { user, api } = useAuth();
  const fetched = useProductCategories();
  const categories = categoriesProp ?? fetched.categories;
  const categoriesLoading = categoriesProp ? false : fetched.loading;
  const categoriesError = categoriesProp ? false : fetched.error;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const pricingRegion = defaultPricingRegion(user);
  const subcategoryFilter = categorySlug ? (categorySlugToId(categorySlug, categories) ?? 'all') : 'all';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const client = user ? api : axios.create({ baseURL: `${API_URL}/api` });
        const res = await client.get('/products');
        if (!cancelled) setProducts(res.data.products || []);
      } catch {
        if (!cancelled) setError('Could not load products. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, user]);

  const navItems = useMemo(
    () => buildStoreNavItems(categories, products, { categoriesFailed: categoriesError }),
    [categories, products, categoriesError],
  );

  const filtered = useMemo(() => {
    if (subcategoryFilter === 'all') return products;
    return products.filter((product) => (product.subcategory || 'other') === subcategoryFilter);
  }, [products, subcategoryFilter]);

  const activeLabel = navItems.find((item) => item.id === subcategoryFilter)?.label || 'All';

  return (
    <div className="min-h-screen bg-muted/20">
      <StoreNav />
      <main className="mx-auto max-w-[1400px] px-6 py-10">
        <div className="mb-8 space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Templates &amp; Toolkits</h1>
          <p className="text-muted-foreground max-w-2xl">
            Professional Word templates and Excel toolkits — buy once, download anytime from My Library.
          </p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setMobileNavOpen((open) => !open)}
              className="flex w-full items-center justify-between rounded-xl border border-border bg-white px-4 py-3 text-sm font-medium shadow-sm"
              data-testid="subcategory-mobile-toggle"
            >
              <span>{categoriesLoading ? 'Loading categories…' : activeLabel}</span>
              <ChevronDown className={`h-4 w-4 transition ${mobileNavOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileNavOpen && !categoriesLoading && (
              <div className="mt-2 rounded-xl border border-border bg-white p-2 shadow-sm">
                <SubcategoryNav
                  items={navItems}
                  activeId={subcategoryFilter}
                  categories={categories}
                  onNavigate={() => setMobileNavOpen(false)}
                />
              </div>
            )}
          </div>

          <aside className="hidden w-56 shrink-0 lg:block">
            <div className="sticky top-24 rounded-xl border border-border bg-white p-3 shadow-sm">
              <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Categories
              </p>
              {categoriesLoading ? (
                <SidebarCategoriesSkeleton />
              ) : (
                <SubcategoryNav items={navItems} activeId={subcategoryFilter} categories={categories} />
              )}
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {loading && (
              <div className="flex min-h-[240px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {!loading && error && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-destructive">
                {error}
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <div className="rounded-xl border border-border bg-white p-12 text-center">
                <Package className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                <p className="text-muted-foreground">No products in this category yet.</p>
              </div>
            )}

            {!loading && !error && filtered.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((product) => (
                  <Link key={product.slug} to={`/templates/${product.slug}`} data-testid={`product-card-${product.slug}`}>
                    <Card className="h-full overflow-hidden transition hover:-translate-y-0.5 hover:shadow-md">
                      <div className="aspect-[4/3] bg-muted flex items-center justify-center">
                        {product.thumbnail_url ? (
                          <img src={resolveThumbnailUrl(product.thumbnail_url)} alt="" className="h-full w-full object-contain" loading="lazy" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-muted-foreground">
                            <Package className="h-10 w-10" />
                          </div>
                        )}
                      </div>
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start justify-between gap-2">
                          <h2 className="font-semibold leading-snug text-foreground line-clamp-2">{product.name}</h2>
                          <Badge variant="secondary" className="shrink-0 capitalize">{product.category}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                        <p className="text-lg font-bold text-primary">{formatProductPrice(product, pricingRegion)}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <PublicSiteFooter />
    </div>
  );
}
